import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Radio, Upload, Alert, Spin, Typography, message, Switch } from 'antd';
import { InboxOutlined, CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { RequirementType, ImageInput } from '../types';
import projectService from '../services/projectService';
import requirementService from '../services/requirementService';
import { filesToImageInputs, validateImages } from '../utils/imageHelpers';
import { extractErrorMessage } from '../utils/errorHandler';

const { Text } = Typography;

interface CreateProjectDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (projectId: string) => void;
}

const CreateProjectDrawer: React.FC<CreateProjectDrawerProps> = ({ open, onClose, onSuccess }) => {
  const [requirementType, setRequirementType] = useState<RequirementType>(RequirementType.DOCUMENT);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageInputs, setImageInputs] = useState<ImageInput[]>([]);
  const [includeFigma, setIncludeFigma] = useState(false);
  const [figmaUrl, setFigmaUrl] = useState<string>('');
  const [figmaAccessToken, setFigmaAccessToken] = useState<string>('');
  const [form] = Form.useForm();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Refs to track actual file counts synchronously (React state is async)
  const documentFilesCountRef = React.useRef(0);
  const imageFilesCountRef = React.useRef(0);

  const checkFormValid = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const values = form.getFieldsValue();
    const hasFiles = !!file || documentFiles.length > 0 || imageFiles.length > 0;
    const hasFigmaUrl = includeFigma && figmaUrl.trim().length > 0 && figmaUrl.includes('figma.com');
    const hasFigmaToken = !includeFigma || figmaAccessToken.trim().length > 0;
    // Allow project creation if either Figma URL+token is valid or any requirement file is present
    setIsFormValid(
      !hasErrors && !!values.projectName && !!values.projectDescription && hasFigmaToken && (hasFigmaUrl || hasFiles)
    );
  };

  useEffect(() => {
    checkFormValid();
    // eslint-disable-next-line
  }, [file, documentFiles, imageFiles, includeFigma, figmaUrl, figmaAccessToken]);

  // Scroll to top when error occurs to make it visible
  useEffect(() => {
    if (error && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const getAcceptedFileTypes = () => {
    switch (requirementType) {
      case RequirementType.AUDIO: return '.mp3,.wav,.m4a';
      case RequirementType.VIDEO: return '.mp4,.avi,.mov';
      case RequirementType.FIGMA_JSON: return '.json';
      case RequirementType.IMAGE: return 'image/jpeg,image/png,image/gif,image/webp';
      default: return '.pdf,.doc,.docx,.txt,.json';
    }
  };

  const getMaxFileCount = () => {
    // IMAGE type: up to 20 images
    // DOCUMENT type: up to 5 document files
    // Others: single file
    if (requirementType === RequirementType.IMAGE) return 20;
    if (requirementType === RequirementType.DOCUMENT) return 5;
    return 1;
  };

  const getMaxFileSize = () => {
    // IMAGE: 3.75MB per file (AWS Bedrock limit)
    // DOCUMENT/AUDIO/VIDEO: 100MB per file
    return requirementType === RequirementType.IMAGE ? 3.75 * 1024 * 1024 : 100 * 1024 * 1024;
  };

  const clearAllFiles = () => {
    setFile(null);
    setDocumentFiles([]);
    setImageFiles([]);
    setImageInputs([]);
    documentFilesCountRef.current = 0;
    imageFilesCountRef.current = 0;
    message.success('All files cleared');
  };

  const handleFileChange = async (info: any) => {
    // Use info.file (the single file that triggered this onChange) instead of
    // info.fileList to avoid duplicates. When multiple files are selected at once,
    // Ant Design fires onChange once per file but info.fileList contains the full
    // batch each time, which caused every file to be appended multiple times.
    const newFile = info.file?.originFileObj || info.file;
    if (!newFile) return;

    // Validate file size
    const maxSize = getMaxFileSize();
    if (newFile.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
      message.error(`File "${newFile.name}" exceeds maximum size of ${maxSizeMB}MB`);
      return;
    }

    if (requirementType === RequirementType.IMAGE) {
      // Handle image file
      const maxImages = getMaxFileCount();
      if (imageFilesCountRef.current >= maxImages) {
        message.error(`Maximum ${maxImages} images allowed. Please remove some before adding more.`);
        return;
      }
      try {
        const validation = await validateImages([newFile]);
        if (!validation.isValid) {
          message.error(validation.error);
          return;
        }
        
        const converted = await filesToImageInputs([newFile]);
        imageFilesCountRef.current += 1;
        setImageFiles(prevImages => [...prevImages, newFile]);
        setImageInputs(prevInputs => [...prevInputs, ...converted]);
        message.success(`Image added successfully (${imageFilesCountRef.current}/${maxImages})`);
      } catch (error: any) {
        message.error(error.message || 'Failed to process image');
      }
    } else if (requirementType === RequirementType.DOCUMENT) {
      // Enforce max file count
      const maxDocs = getMaxFileCount();
      if (documentFilesCountRef.current >= maxDocs) {
        message.error(`Maximum ${maxDocs} documents allowed. Please remove some before adding more.`);
        return;
      }
      // Handle document file — skip if already added (same name + size)
      let wasDuplicate = false;
      setDocumentFiles(prevDocs => {
        const isDuplicate = prevDocs.some(
          (d) => d.name === newFile.name && d.size === newFile.size
        );
        if (isDuplicate) {
          wasDuplicate = true;
          return prevDocs;
        }
        return [...prevDocs, newFile];
      });
      if (wasDuplicate) return;
      documentFilesCountRef.current += 1;
      // Set the first document as the main file for backward compatibility
      if (!file) {
        setFile(newFile);
      }
      message.success(`Document "${newFile.name}" added successfully (${documentFilesCountRef.current}/${maxDocs})`);
    } else {
      // Single file for AUDIO, VIDEO, FIGMA_JSON
      setFile(newFile);
    }
    setError(null);
  };

  const handleDrawerClose = () => {
    setRequirementType(RequirementType.DOCUMENT);
    setFile(null);
    setDocumentFiles([]);
    setImageFiles([]);
    setImageInputs([]);
    documentFilesCountRef.current = 0;
    imageFilesCountRef.current = 0;
    setIncludeFigma(false);
    setFigmaUrl('');
    setError(null);
    setLoading(false);
    form.resetFields();
    onClose();
  };

  // Helper function to check if a rules file exists in uploaded files
  const hasRulesFile = (): boolean => {
    // Check if any document file has "rules" in its name (case-insensitive)
    const allFiles = [...documentFiles];
    if (file) allFiles.push(file);
    return allFiles.some(f => /\brules\b/i.test(f.name));
  };

  // Show confirmation dialog when no rules file is present
  const showNoRulesConfirmation = (values: any): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'No Custom Rules File Detected',
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        content: (
          <div>
            <p style={{ marginBottom: '12px' }}>
              You have not uploaded a custom rules file (e.g., rules.txt).
            </p>
            <p style={{ marginBottom: '0' }}>
              <strong>Are you willing to proceed without custom rules?</strong>
            </p>
            <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
              If not provided, code will be generated using business rules defined in the tool.
            </p>
          </div>
        ),
        okText: 'Proceed Without Rules',
        cancelText: 'Cancel',
        centered: true,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        okButtonProps: {
          style: {
            background: '#d32f2f',
            borderColor: '#d32f2f'
          }
        }
      });
    });
  };

  const handleSubmit = async (values: any) => {
    // Check if rules file is present; if not, show confirmation
    if (!hasRulesFile()) {
      const shouldProceed = await showNoRulesConfirmation(values);
      if (!shouldProceed) {
        return; // User cancelled, don't proceed
      }
    }

    // Allow project creation if either Figma URL is valid or any requirement file is present
    const hasAnyFiles = file || documentFiles.length > 0 || imageFiles.length > 0;
    const hasFigmaUrl = includeFigma && figmaUrl.trim().length > 0 && figmaUrl.includes('figma.com');
    if (!hasAnyFiles && !hasFigmaUrl) {
      setError('Please provide a Figma URL or upload at least one requirement file.');
      return;
    }
    if (includeFigma && (!figmaUrl.trim() || !figmaUrl.includes('figma.com'))) {
      setError('Please enter a valid Figma design URL.');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadStatus('');
    setIsTranscribing(false);
    
    try {
      setUploadStatus('Creating project...');
      
      // Determine the actual requirement type for the project
      let actualRequirementType = requirementType;
      
      // If IMAGE type selected, use DOCUMENT for project creation (images are supplementary)
      // If DOCUMENT type and only images uploaded, use IMAGE type
      if (requirementType === RequirementType.IMAGE && imageFiles.length > 0) {
        actualRequirementType = RequirementType.IMAGE;
      } else if (requirementType === RequirementType.DOCUMENT && documentFiles.length === 0 && imageFiles.length > 0) {
        actualRequirementType = RequirementType.IMAGE;
      }
      
      const projectResponse = await projectService.createProject({
        name: values.projectName,
        description: values.projectDescription,
        requirementType: actualRequirementType,
        ...(includeFigma && figmaUrl.trim() ? { figmaDesignUrl: figmaUrl.trim(), figmaAccessToken: figmaAccessToken.trim() } : {}),
      });
      
      if (!projectResponse || projectResponse.status !== 'success') {
        throw new Error('Failed to create project. Please try again.');
      }
      const projectId = projectResponse.data.id;

      // Determine the main file to upload (for backward compatibility)
      // Priority: explicit file > first document > use IMAGE type with no main file
      const mainFile = file || (documentFiles.length > 0 ? documentFiles[0] : null);
      
      // Map FIGMA_JSON to DOCUMENT for backend processing
      let backendRequirementType = actualRequirementType === RequirementType.FIGMA_JSON 
        ? RequirementType.DOCUMENT 
        : actualRequirementType;

      // If IMAGE type and no main file, we'll handle differently
      const isImageOnly = actualRequirementType === RequirementType.IMAGE && !mainFile;
      
      if (isImageOnly) {
        // For IMAGE-only upload, use first image as main file
        if (imageFiles.length === 0) {
          throw new Error('No images selected');
        }
        // Convert first image to main file for upload
        const firstImage = imageFiles[0];
        setUploadStatus('Uploading images...');
        
        const uploadResponse = await requirementService.uploadRequirement(
          projectId, 
          RequirementType.IMAGE,
          firstImage,
          imageInputs
        );
        
        if (!uploadResponse || uploadResponse.status !== 'success') {
          throw new Error('Failed to upload images. Please try again.');
        }
        
        message.success(`${imageFiles.length} ${imageFiles.length === 1 ? 'image' : 'images'} uploaded successfully!`);
        handleDrawerClose();
        if (onSuccess) onSuccess(projectId);
        return;
      }

      // If only Figma (no files), just create the project — extraction is a separate step
      if (!mainFile && !imageFiles.length && includeFigma && figmaUrl.trim()) {
        message.success('Project created with Figma design URL. You can now extract the design from the project view.');
        handleDrawerClose();
        if (onSuccess) onSuccess(projectId);
        return;
      }
      // Standard upload flow with main file
      if (!mainFile) {
        throw new Error('No main file available for upload');
      }

      // Check if it's audio/video to show transcription status
      const fileExtension = mainFile.name.split('.').pop()?.toLowerCase();
      const isAudioVideo = ['mp3', 'wav', 'mp4', 'avi', 'm4a', 'aac', 'flac', 'ogg', 'mov', 'wmv'].includes(fileExtension || '');
      
      if (isAudioVideo) {
        setUploadStatus('Uploading file to cloud storage...');
      } else {
        setUploadStatus('Uploading files...');
      }

      // Detect rules files by name — a file whose stem matches "rules" (case-insensitive)
      // is uploaded as RULES type so the backend stores it separately from the BRD.
      const resolveFileType = (f: File): RequirementType => {
        if (backendRequirementType !== RequirementType.DOCUMENT) return backendRequirementType;
        const stem = f.name.replace(/\.[^/.]+$/, '');
        return /\brules\b/i.test(stem) ? RequirementType.RULES : RequirementType.DOCUMENT;
      };

      const uploadResponse = await requirementService.uploadRequirement(
        projectId,
        resolveFileType(mainFile),
        mainFile,
        imageInputs.length > 0 ? imageInputs : undefined
      );

      if (!uploadResponse || uploadResponse.status !== 'success') {
        throw new Error('Failed to upload requirement. Please try again.');
      }

      // Upload any additional document files (e.g. rules.txt uploaded alongside the main BRD).
      // Filter out the mainFile to avoid uploading it twice (don't rely on index
      // because React state race conditions can make file !== documentFiles[0]).
      if (documentFiles.length > 1) {
        setUploadStatus('Uploading additional files...');
        const additionalFiles = documentFiles.filter(f => f !== mainFile);
        for (const extraFile of additionalFiles) {
          const extraResp = await requirementService.uploadRequirement(
            projectId, resolveFileType(extraFile), extraFile
          );
          if (!extraResp || extraResp.status !== 'success') {
            throw new Error(`Failed to upload ${extraFile.name}. Please try again.`);
          }
        }
      }

      // Build success message
      let successMsg = '';
      const docCount = documentFiles.length || (file ? 1 : 0);
      const imgCount = imageFiles.length;
      
      if (docCount > 0 && imgCount > 0) {
        successMsg = `${docCount} document(s) and ${imgCount} image(s) uploaded successfully!`;
      } else if (docCount > 0) {
        successMsg = `${docCount} ${docCount === 1 ? 'document' : 'documents'} uploaded successfully!`;
      } else if (imgCount > 0) {
        successMsg = `${imgCount} ${imgCount === 1 ? 'image' : 'images'} uploaded successfully!`;
      } else {
        successMsg = 'File uploaded successfully!';
      }

      if (isAudioVideo) {
        setIsTranscribing(true);
        setUploadStatus('Transcription in progress... This may take a few minutes.');
        // Give user feedback that transcription is happening in background
        setTimeout(() => {
          const figmaMsg = (includeFigma && figmaUrl.trim()) ? ' You can extract the Figma design from the project view.' : '';
          message.success(successMsg + figmaMsg + ' Transcription is processing in the background.');
          handleDrawerClose();
          if (onSuccess) onSuccess(projectId);
        }, 1500);
      } else {
        const figmaMsg = (includeFigma && figmaUrl.trim()) ? ' You can now extract the Figma design from the project view.' : '';
        message.success(successMsg + figmaMsg);
        handleDrawerClose();
        if (onSuccess) onSuccess(projectId);
      }
    } catch (err: any) {
      console.error('Project creation error:', err);
      // Extract user-friendly error message from backend response
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      setUploadStatus('');
      setIsTranscribing(false);
      setLoading(false);
      // Scroll to top to show error message
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Do NOT close drawer or call onSuccess when there's an error
      return;
    } finally {
      if (!isTranscribing) {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleDrawerClose}
      footer={null}
      closable={false}
      centered
      width={520}
      destroyOnClose
      maskStyle={{
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(5px)'
      }}
      bodyStyle={{ padding: 0 }}
      style={{ top: 0 }}
    >
      <div style={{
        background: 'white',
        borderRadius: '8px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        border: '1px solid #e5e7eb'
      }}>
        {/* Modal Content */}
        <div 
          ref={scrollContainerRef}
          style={{
            padding: '30px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
        >
          <Spin spinning={loading} tip={uploadStatus || 'Processing...'}>
            {error && (
              <Alert 
                type="error" 
                message={error}
                closable
                onClose={() => setError(null)}
                style={{ 
                  marginBottom: 20,
                  borderRadius: '6px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} 
              />
            )}
            {uploadStatus && !error && (
              <Alert 
                type="info" 
                message={uploadStatus}
                description={isTranscribing ? 'Your file is being transcribed. You can continue working while this completes in the background.' : undefined}
                closable
                onClose={() => setUploadStatus('')}
                style={{ 
                  marginBottom: 20,
                  borderRadius: '6px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }} 
              />
            )}
            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              initialValues={{
                projectName: '',
                projectDescription: '',
              }}
              onFieldsChange={checkFormValid}
            >
              {/* Project Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#111827',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Project Name
                </label>
                <Form.Item
                  name="projectName"
                  rules={[{ required: true, message: 'Please enter project name' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input 
                    placeholder="Enter project name..."
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#d32f2f';
                      e.target.style.boxShadow = '0 0 0 3px rgba(211, 47, 47, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </div>

              {/* Project Description */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#111827',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Description
                </label>
                <Form.Item
                  name="projectDescription"
                  rules={[{ required: true, message: 'Please enter project description' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea 
                    rows={3}
                    placeholder="Describe your project..."
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#d32f2f';
                      e.target.style.boxShadow = '0 0 0 3px rgba(211, 47, 47, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </Form.Item>
              </div>

              {/* Figma Design Toggle */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: includeFigma ? '#fef3c7' : '#ffffff',
                  borderColor: includeFigma ? '#f59e0b' : '#d1d5db',
                  transition: 'all 0.3s ease',
                }}>
                  <div>
                    <span style={{
                      color: '#111827',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}>
                      🎨 Include Figma Design
                    </span>
                    <p style={{
                      margin: '4px 0 0 0',
                      color: '#6b7280',
                      fontSize: '12px',
                    }}>
                      Extract UI specifications from a Figma design URL
                    </p>
                  </div>
                  <Switch
                    checked={includeFigma}
                    onChange={(checked) => {
                      setIncludeFigma(checked);
                      if (checked && requirementType === RequirementType.IMAGE) {
                        setRequirementType(RequirementType.DOCUMENT);
                      }
                    }}
                    style={{
                      backgroundColor: includeFigma ? '#d32f2f' : '#d1d5db',
                    }}
                  />
                </div>
              </div>

              {/* Figma URL Input */}
              {includeFigma && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}>
                    Figma Design URL <span style={{ color: '#d32f2f' }}>*</span>
                  </label>
                  <Input 
                    placeholder="https://www.figma.com/design/..."
                    value={figmaUrl}
                    onChange={(e) => {
                      setFigmaUrl(e.target.value);
                      checkFormValid();
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#d32f2f';
                      e.target.style.boxShadow = '0 0 0 3px rgba(211, 47, 47, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <p style={{
                    margin: '6px 0 0 0',
                    color: '#6b7280',
                    fontSize: '12px',
                  }}>
                    Paste your Figma design, file, or prototype URL. You can create a project with only Figma, only requirement, or both.
                  </p>

                  <label style={{
                    display: 'block',
                    marginTop: '16px',
                    marginBottom: '8px',
                    color: '#111827',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}>
                    Figma Access Token <span style={{ color: '#d32f2f' }}>*</span>
                  </label>
                  <Input.Password
                    placeholder="figd_xxxxxxxxxxxxxxxx"
                    value={figmaAccessToken}
                    onChange={(e) => {
                      setFigmaAccessToken(e.target.value);
                      checkFormValid();
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: '#ffffff',
                      color: '#111827',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#d32f2f';
                      e.target.style.boxShadow = '0 0 0 3px rgba(211, 47, 47, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <p style={{
                    margin: '6px 0 0 0',
                    color: '#6b7280',
                    fontSize: '12px',
                  }}>
                    Go to Figma → Settings → Personal Access Tokens → Generate a new token.
                  </p>
                </div>
              )}

              {/* Requirement Type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#111827',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Requirement Type
                </label>
                <Radio.Group
                  value={requirementType}
                  onChange={e => setRequirementType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label style={{ cursor: 'pointer' }}>
                      <Radio value={RequirementType.DOCUMENT} style={{ display: 'none' }} />
                      <span style={{
                        display: 'block',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        background: requirementType === RequirementType.DOCUMENT ? '#fee2e2' : '#ffffff',
                        borderColor: requirementType === RequirementType.DOCUMENT ? '#d32f2f' : '#d1d5db',
                        color: requirementType === RequirementType.DOCUMENT ? '#dc2626' : '#111827',
                        fontWeight: requirementType === RequirementType.DOCUMENT ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (requirementType !== RequirementType.DOCUMENT) {
                          e.currentTarget.style.borderColor = '#d32f2f';
                          e.currentTarget.style.background = '#fee2e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requirementType !== RequirementType.DOCUMENT) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                      >
                        📄 Document (Word, Text)
                      </span>
                    </label>
                    {/* <label style={{ cursor: 'pointer' }}>
                      <Radio value={RequirementType.FIGMA_JSON} style={{ display: 'none' }} />
                      <span style={{
                        display: 'block',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        background: requirementType === RequirementType.FIGMA_JSON ? '#fee2e2' : '#ffffff',
                        borderColor: requirementType === RequirementType.FIGMA_JSON ? '#d32f2f' : '#d1d5db',
                        color: requirementType === RequirementType.FIGMA_JSON ? '#dc2626' : '#111827',
                        fontWeight: requirementType === RequirementType.FIGMA_JSON ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (requirementType !== RequirementType.FIGMA_JSON) {
                          e.currentTarget.style.borderColor = '#d32f2f';
                          e.currentTarget.style.background = '#fee2e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requirementType !== RequirementType.FIGMA_JSON) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                      >
                        📋 Figma File (JSON)
                      </span>
                    </label> */}
                    <label style={{ cursor: 'pointer' }}>
                      <Radio value={RequirementType.AUDIO} style={{ display: 'none' }} />
                      <span style={{
                        display: 'block',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        background: requirementType === RequirementType.AUDIO ? '#fee2e2' : '#ffffff',
                        borderColor: requirementType === RequirementType.AUDIO ? '#d32f2f' : '#d1d5db',
                        color: requirementType === RequirementType.AUDIO ? '#dc2626' : '#111827',
                        fontWeight: requirementType === RequirementType.AUDIO ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (requirementType !== RequirementType.AUDIO) {
                          e.currentTarget.style.borderColor = '#d32f2f';
                          e.currentTarget.style.background = '#fee2e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requirementType !== RequirementType.AUDIO) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                      >
                        🎵 Audio Recording (MP3, WAV)
                      </span>
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <Radio value={RequirementType.VIDEO} style={{ display: 'none' }} />
                      <span style={{
                        display: 'block',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        background: requirementType === RequirementType.VIDEO ? '#fee2e2' : '#ffffff',
                        borderColor: requirementType === RequirementType.VIDEO ? '#d32f2f' : '#d1d5db',
                        color: requirementType === RequirementType.VIDEO ? '#dc2626' : '#111827',
                        fontWeight: requirementType === RequirementType.VIDEO ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (requirementType !== RequirementType.VIDEO) {
                          e.currentTarget.style.borderColor = '#d32f2f';
                          e.currentTarget.style.background = '#fee2e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requirementType !== RequirementType.VIDEO) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                      >
                        🎥 Video Recording (MP4, AVI)
                      </span>
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <Radio value={RequirementType.IMAGE} style={{ display: 'none' }} />
                      <span style={{
                        display: 'block',
                        padding: '12px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        transition: 'all 0.3s ease',
                        textAlign: 'center',
                        background: requirementType === RequirementType.IMAGE ? '#fee2e2' : '#ffffff',
                        borderColor: requirementType === RequirementType.IMAGE ? '#d32f2f' : '#d1d5db',
                        color: requirementType === RequirementType.IMAGE ? '#dc2626' : '#111827',
                        fontWeight: requirementType === RequirementType.IMAGE ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (requirementType !== RequirementType.IMAGE) {
                          e.currentTarget.style.borderColor = '#d32f2f';
                          e.currentTarget.style.background = '#fee2e2';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requirementType !== RequirementType.IMAGE) {
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.background = '#ffffff';
                        }
                      }}
                      >
                        🖼️ Images (JPEG, PNG, GIF, WebP)
                      </span>
                    </label>
                  </div>
                </Radio.Group>
              </div>

              {/* File Upload */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#111827',
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Upload {requirementType === RequirementType.IMAGE ? 'Images' : 'File'}
                </label>
                {(documentFiles.length > 0 || imageFiles.length > 0) && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: '#e8f5e9',
                    border: '1px solid #4caf50',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#2e7d32',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <strong>✓ Files Selected:</strong>
                      {documentFiles.length > 0 && ` ${documentFiles.length} document(s)`}
                      {documentFiles.length > 0 && imageFiles.length > 0 && ' + '}
                      {imageFiles.length > 0 && `${imageFiles.length} image(s)`}
                    </div>
                    <Button 
                      type="text" 
                      size="small" 
                      danger
                      onClick={clearAllFiles}
                      style={{ fontSize: '12px' }}
                    >
                      Clear All
                    </Button>
                  </div>
                )}
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '40px 20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: '#f9fafb'
                }}>
                  <Upload.Dragger
                    accept={getAcceptedFileTypes()}
                    beforeUpload={() => false}
                    multiple={requirementType === RequirementType.IMAGE || requirementType === RequirementType.DOCUMENT}
                    maxCount={getMaxFileCount()}
                    onChange={handleFileChange}
                    fileList={[]}
                    showUploadList={false}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0
                    }}
                  >
                    <div style={{
                      fontSize: '32px',
                      marginBottom: '12px',
                      color: '#6b7280'
                    }}>
                      {requirementType === RequirementType.IMAGE ? '🖼️' : '📁'}
                    </div>
                    <p style={{
                      margin: 0,
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      Drag & drop {requirementType === RequirementType.IMAGE ? 'images' : 'files'} here or <button type="button" style={{
                        color: '#d32f2f',
                        background: 'none',
                        border: 'none',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>browse</button>
                    </p>
                    <p style={{
                      margin: '8px 0 0 0',
                      color: '#9ca3af',
                      fontSize: '12px'
                    }}>
                      {requirementType === RequirementType.IMAGE 
                        ? 'JPEG, PNG, GIF, WebP'
                        : requirementType === RequirementType.DOCUMENT
                        ? 'Word, Text • Max 5 files'
                        : 'Max file size: 100MB'
                      }
                    </p>
                  </Upload.Dragger>
                </div>
              </div>
            </Form>
          </Spin>
        </div>

        {/* Modal Footer */}
        <div style={{
          padding: '25px 30px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
          background: '#f9fafb'
        }}>
          <Button
            onClick={handleDrawerClose}
            disabled={loading}
            style={{
              padding: '12px 24px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              background: 'white',
              color: '#6b7280',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#9ca3af';
              e.currentTarget.style.color = '#111827';
              e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#6b7280';
              e.currentTarget.style.background = 'white';
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
            disabled={loading || !isFormValid}
            style={{
              padding: '12px 24px',
              border: 'none',
              borderRadius: '6px',
              background: !isFormValid || loading ? '#f3f4f6' : '#d32f2f',
              color: !isFormValid || loading ? '#9ca3af' : 'white',
              fontWeight: 500,
              cursor: !isFormValid || loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (isFormValid && !loading) {
                e.currentTarget.style.background = '#b71c1c';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (isFormValid && !loading) {
                e.currentTarget.style.background = '#d32f2f';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            Create Project
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateProjectDrawer;